import React, { useEffect } from "react"
import { useState } from "react";
import * as _ from "lodash";
import { Button, Form, InputGroup, Modal } from "react-bootstrap"
import ImpersonationApiKey, { UserIdentifierType, getUserIdentifierTypeName } from "./impersonationApiKey";
import ErrorList from "common/ui/ErrorList";
import TypedSelect from "common/ui/TypedSelect";

export interface AddImpersonationApiKeyModalProps {
    apiKey: ImpersonationApiKey;
    showModal: boolean;
    onClose(): void;
    onSubmit(apiKey: ImpersonationApiKey): void;
    errors: string[];
}

const identifierOptions = [UserIdentifierType.Email, UserIdentifierType.AccountId]
    .map(m => ({ id: m, name: getUserIdentifierTypeName(m)}));

const AddImpersonationApiKeyModal: React.FC<AddImpersonationApiKeyModalProps> = props => {

    const [apiKey, setApiKey] = useState<ImpersonationApiKey>(null);
    const [isFormValidated, setIsFormValidated] = useState(false);

    useEffect(() => {
        setApiKey(props.apiKey);
        setIsFormValidated(false);
    }, [props.apiKey])

    const formRef = React.createRef<HTMLFormElement>();
    
    function onClose() {
        props.onClose();
    }

    function onCreate() {

        if (formRef.current.checkValidity()) {
            props.onSubmit(apiKey);
        }

        setIsFormValidated(true);
    }

    if (apiKey == null) return null;

    return <Modal show={props.showModal} onHide={onClose} backdrop="static" >
        <Modal.Header closeButton>
            <Modal.Title>
                Add Impersonation Key
            </Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form ref={formRef} validated={isFormValidated}>
                <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control 
                        value={apiKey.name ?? ''}
                        maxLength={50}
                        required
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setApiKey({...apiKey, name: ev.target.value })}
                        />
                </Form.Group>
                
                <Form.Group>
                    <Form.Label>User Identifier</Form.Label>
                    <TypedSelect
                        value={{id: apiKey.userIdentifierType}}
                        options={identifierOptions}
                        valueGetter={m => m.id}
                        textGetter={m => m.name}
                        onValueSelected={m => setApiKey({...apiKey, userIdentifierType: m.id})}
                        required
                        showNullRow
                        />
                </Form.Group>

                <Form.Group>
                    <Form.Label>Token Lifetime</Form.Label>
                    <InputGroup>
                        <Form.Control type="number"
                            value={apiKey.tokenLifetimeInSeconds}
                            min={60 * 15}
                            max={60 * 60 * 48}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setApiKey({...apiKey, tokenLifetimeInSeconds: ev.target.valueAsNumber })}
                            />
                        <InputGroup.Append>
                            <InputGroup.Text>seconds</InputGroup.Text>
                        </InputGroup.Append>
                    </InputGroup>
                </Form.Group>
                
                <Form.Group>
                    <Form.Label className="optional">Notes</Form.Label>
                    <Form.Control as="textarea"
                        value={apiKey.notes ?? ''}
                        maxLength={500}
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setApiKey({...apiKey, notes: ev.target.value })}
                        />
                </Form.Group>
            </Form>
        
            <ErrorList errors={props.errors}>
                <h3>Errors</h3>
            </ErrorList>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={onClose}>
                Cancel
            </Button>
            <Button variant="primary" onClick={onCreate}>
                Create
            </Button>
        </Modal.Footer>
    </Modal>
}

export default AddImpersonationApiKeyModal;